import { Dispatch } from 'redux';
import GoogleController from '../controllers/GoogleController';
import commonHandler from '../errorHandlers/common-handler';
import { googleSheetsHandler } from '../errorHandlers/google-sheets-handler';
import { OauthUrlResponse } from '../interfaces/googleSheet';
import { activeFacebookPageIdSelector } from '../selectors/viewSelectors';
import { StoreState } from '../store-interfaces';
import { getActionHookBuilder } from './action-hook-builder';
import connectionsCRUDActions from './ConnectionsCRUDActions';
import CrudActionContainer from './CrudActionContainer';

class GoogleSheetsCRUDActions extends CrudActionContainer {
  retrieveOauthUrlAction: string;
  retrieveAccounts: string;
  retrieveSpreadsheets: string;
  retrieveSheets: string;
  retrieveColumns: string;
  revokeAccess: string;

  constructor() {
    super('GOOGLE_SHEETS');
    this.retrieveOauthUrlAction = this.makeActionKey('RETRIEVED_OAUTH_URL');
    this.retrieveAccounts = this.makeActionKey('RETRIEVED_ACCOUNTS');
    this.retrieveSpreadsheets = this.makeActionKey('RETRIEVED_SPREADSHEETS');
    this.retrieveSheets = this.makeActionKey('RETRIEVED_SHEETS');
    this.retrieveColumns = this.makeActionKey('RETRIEVED_COLUMNS');
    this.revokeAccess = this.makeActionKey('REVOKE_ACCESS');
  }

  getOauthUrl(redirectTo: string) {
    return async (
      dispatch: Dispatch<StoreState>,
      getState: () => StoreState,
    ) => {
      const state = getState();
      const facebookPageId = activeFacebookPageIdSelector(state);
      dispatch(this.busy(this.retrieveOauthUrlAction));
      try {
        const response: OauthUrlResponse = await GoogleController.getOauthUrl(
          redirectTo,
          'sheets',
          facebookPageId,
        );
        dispatch({
          type: this.retrieveOauthUrlAction,
        });

        return response.link;
      } catch (error) {
        commonHandler(dispatch)(error);
      } finally {
        this.unbusy();
      }
    };
  }

  getAccounts(facebookPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveAccounts));
      try {
        const response = await GoogleController.getAccounts(facebookPageId);
        dispatch({
          type: this.retrieveAccounts,
          accounts: response,
        });
        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  getSpreadsheets(id: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveSpreadsheets));
      try {
        const response = await GoogleController.getSpreadsheets(id);
        dispatch({
          type: this.retrieveSpreadsheets,
          spreadsheets: response,
        });
        return response;
      } catch (error) {
        googleSheetsHandler(dispatch)({ error });
      }
    };
  }

  getSheets(accountId: number, spreadsheetId: string) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveSheets));
      try {
        const response = await GoogleController.getSheets(
          accountId,
          spreadsheetId,
        );

        dispatch({
          type: this.retrieveSheets,
          sheets: response,
        });
        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }
  getColumns(botId: number, webhookId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.retrieveColumns));
      try {
        const response = await GoogleController.getColumns(botId, webhookId);

        dispatch({
          type: this.retrieveColumns,
          columns: response,
        });
        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }

  disconnectAccount(accountId: number, fbPageId: number) {
    return async (dispatch: Dispatch<StoreState>) => {
      dispatch(this.busy(this.revokeAccess));
      try {
        const response = await GoogleController.revokeAccess(accountId);

        dispatch({
          type: this.revokeAccess,
        });
        dispatch(this.getAccounts(fbPageId));
        dispatch(
          connectionsCRUDActions.retrieveAll(fbPageId, {
            type: 'google_spreadsheets',
            pageSize: 9999,
          }),
        );
        return response;
      } catch (error) {
        commonHandler(dispatch)(error);
      }
    };
  }
}

const googleSheetsCRUDActions = new GoogleSheetsCRUDActions();

export default googleSheetsCRUDActions;

export const useGoogleSheetsCRUDActions = getActionHookBuilder(
  googleSheetsCRUDActions,
  [
    'getOauthUrl',
    'getAccounts',
    'getSpreadsheets',
    'getSheets',
    'getColumns',
    'disconnectAccount',
  ],
);
